import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["rule"];

  connect() {
    this.form = this.element.closest("form");
    this.rules = this.ruleTargets.map((ruleTarget) => ({
      effect: ruleTarget.dataset.effect,
      conditions: JSON.parse(ruleTarget.dataset.conditions),
    }));

    // Track if this section should be hidden
    this.isHidden = false;

    // Setup event delegation to capture all field changes without needing targets
    this.form.addEventListener("change", this.handleFieldChange.bind(this));
    this.form.addEventListener("input", this.handleFieldChange.bind(this));

    // Watch for DOM mutations (React re-renders)
    this.observer = new MutationObserver((mutations) => {
      // If the section should be hidden, re-apply the effect
      if (this.isHidden) {
        this.applyHiddenState();
      }
    });

    // Observe changes to child elements
    this.observer.observe(this.element, {
      childList: true,
      subtree: true,
      attributes: false,
    });

    // Initial evaluation of rules
    this.evaluateAllRules();
  }

  disconnect() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  // Centralized field change handler - catches all field changes via event delegation
  handleFieldChange(event) {
    const field = event.target;

    // Only handle form control elements
    if (!["INPUT", "SELECT", "TEXTAREA"].includes(field.tagName)) {
      return;
    }

    // Don't interfere with fields that are inside rules sections (conditional fields)
    // Only react to changes from fields outside rules sections
    if (field.closest('[data-controller*="rules"]') === this.element) {
      // This field is inside this rules section, ignore it to prevent interference
      return;
    }

    // Re-evaluate all rules when any field changes
    this.evaluateAllRules();
  }

  // Get all form field values without requiring targets
  getFormValues() {
    const values = {};

    // Get all input, select, and textarea elements within the form
    const fields = this.form.querySelectorAll("input, select, textarea");

    fields.forEach((field) => {
      const name = field.name;
      if (!name) return; // Skip fields without names

      if (field.type === "checkbox") {
        // For checkboxes, always use the checkbox value (not hidden field)
        // This handles the Rails pattern of hidden field + checkbox with same name
        values[name] = field.checked ? field.value : "0"; // "1" when checked, "0" when unchecked
      } else if (field.type === "radio") {
        if (field.checked) {
          values[name] = field.value;
        }
      } else if (field.type === "hidden") {
        // Only use hidden field value if there's no checkbox with the same name
        // (checkboxes will overwrite this later in the loop)
        if (!values.hasOwnProperty(name)) {
          values[name] = field.value;
        }
      } else if (field.type === "file") {
        values[name] = field.files;
      } else {
        values[name] = field.value;
      }
    });

    return values;
  }

  // Evaluate all conditional rules
  evaluateAllRules() {
    const formValues = this.getFormValues();
    this.rules.forEach((rule) => {
      const shouldApplyEffect = this.evaluateConditions(
        rule.conditions,
        formValues,
      );
      this.applyRuleEffect(rule.effect, shouldApplyEffect);
    });
  }

  // Evaluate a set of conditions against current form values
  // Adapted for your specific rule format
  evaluateConditions(conditions, formValues) {
    return conditions.every((condition) => {
      const { key, operator, values } = condition;
      const fieldValue = formValues[key];

      switch (operator) {
        case "EQ": // Equals any value in the array
          return values.includes(fieldValue);
        case "NEQ": // Not equal to any value in the array
          return !values.includes(fieldValue);
        case "IN": // Field value is in the values array
          return values.includes(fieldValue);
        case "NIN": // Field value is not in the values array
          return !values.includes(fieldValue);
        default:
          console.warn(`Unknown operator: ${operator}`);
          return false;
      }
    });
  }

  // Apply rule effects based on condition evaluation
  applyRuleEffect(effect, shouldApply) {
    switch (effect) {
      case "SHOW":
        this.isHidden = !shouldApply;
        this.toggleClass(shouldApply);
        this.toggleFieldsDisabled(!shouldApply);
        break;
      case "HIDE":
        this.isHidden = shouldApply;
        this.toggleClass(!shouldApply);
        this.toggleFieldsDisabled(shouldApply);
        break;
      case "ENABLE":
        this.element.disabled = !shouldApply;
        break;
      case "DISABLE":
        this.element.disabled = shouldApply;
        break;
      case "ADD_CLASS":
        const classToAdd = additionalParams[0] || "";
        if (classToAdd) {
          shouldApply
            ? this.element.classList.add(classToAdd)
            : this.element.classList.remove(classToAdd);
        } else {
          console.warn("ADD_CLASS effect missing class name:", effect);
        }
        break;
      case "REMOVE_CLASS":
        const classToRemove = additionalParams[0] || "";
        if (classToRemove) {
          shouldApply
            ? this.element.classList.remove(classToRemove)
            : this.element.classList.add(classToRemove);
        } else {
          console.warn("REMOVE_CLASS effect missing class name:", effect);
        }
        break;
      default:
        console.warn(`Unknown effect type: ${effect}`);
    }
  }

  toggleClass(shouldApply) {
    if (shouldApply) {
      this.element.classList.remove("hidden");
    } else {
      this.element.classList.add("hidden");
    }
  }

  toggleFieldsDisabled(shouldDisable) {
    // Find all form INPUT fields within this rules element (NOT buttons - they don't submit data)
    const fields = this.element.querySelectorAll(
      "input, select, textarea",
    );

    fields.forEach((field) => {
      if (shouldDisable) {
        // When hiding, disable form inputs and remove their name to prevent submission
        field.disabled = true;
        if (field.name) {
          field.dataset.originalName = field.name;
          field.removeAttribute("name");
        }
      } else {
        // When showing, enable form inputs and restore their name
        field.disabled = false;
        if (field.dataset.originalName) {
          field.name = field.dataset.originalName;
          delete field.dataset.originalName;
        }
      }
    });
  }

  applyHiddenState() {
    // Re-apply the hidden state after React re-render
    setTimeout(() => {
      if (this.isHidden) {
        // Disable form inputs (NOT buttons - they don't submit data)
        const fields = this.element.querySelectorAll(
          "input, select, textarea",
        );
        fields.forEach((field) => {
          field.disabled = true;
          if (field.name) {
            field.dataset.originalName = field.name;
            field.removeAttribute("name");
          }
        });
      }
    }, 0); // Use setTimeout to ensure React has finished rendering
  }
}
